import React, { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
	MoreVertical,
	BotOff,
	Bot,
	SlidersHorizontal,
	AlarmClock,
	AlarmClockOff,
	ArrowRightLeft,
	KanbanSquare,
	History,
	Check,
	MessageSquare,
	Link2,
} from "lucide-react";

import { Button } from "../ui/button";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuSub,
	DropdownMenuSubContent,
	DropdownMenuSubTrigger,
	DropdownMenuTrigger,
} from "../ui/dropdown-menu";

import { i18n } from "../../translate/i18n";
import { toast } from "react-toastify";
import api from "../../services/api";
import { avisarContadores } from "../../services/contadoresDaLista";
import ConfirmationModal from "../ConfirmationModal";
import TransferTicketModal from "../TransferTicketModal";
import TicketAutomationModal from "../TicketAutomationModal";
import toastError from "../../errors/toastError";
import { Can } from "../Can";
import { AuthContext } from "../../context/Auth/AuthContext";
import AdicionarAoKanbanModal from "../AdicionarAoKanbanModal";
import usePermissions from "../../hooks/usePermissions";
import useWhatsApps from "../../hooks/useWhatsApps";
import usePrefTickets from "../../hooks/usePrefTickets";
import useAtalhoDeTeclado from "../../hooks/useAtalhoDeTeclado";
import {
	ACOES_DA_BARRA,
	ACOES_PADRAO,
} from "../TicketActionButtons/acoesDaBarra";
import {
	BOTOES_DA_CONVERSA,
	IDS_BOTOES_DA_CONVERSA,
} from "../Ticket/botoesDaConversa";

const TicketOptionsMenu = ({ ticket }) => {
	const [menuOpen, setMenuOpen] = useState(false);
	const [funnelModalOpen, setFunnelModalOpen] = useState(false);
	const [confirmationOpen, setConfirmationOpen] = useState(false);
	const [transferTicketModalOpen, setTransferTicketModalOpen] = useState(false);
	const isMounted = useRef(true);
	const { user } = useContext(AuthContext);
	const navigate = useNavigate();

	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	const handleDeleteTicket = async () => {
		try {
			await api.delete(`/tickets/${ticket.id}`);
		} catch (err) {
			toastError(err);
		}
	};

	// Cala o robô só nesta conversa. Quem está atendendo às vezes precisa que
	// o fluxo, o bot da fila e a IA parem de responder por cima dele — antes
	// isso só era possível desligando na conexão, o que afeta todo mundo.
	const [pausado, setPausado] = useState(Boolean(ticket.botPaused));
	// Os atalhos da barra moram aqui porque aqui já estão os modais e as
	// funções de cada ação — o botão fixado dispara exatamente o mesmo caminho
	// do item do menu, sem uma segunda cópia para sair de sincronia.
	// O terceiro item são os atalhos que o CARGO fixou: o admin decidiu, e aqui
	// eles aparecem marcados/desmarcados sem poder mudar.
	const [acoesBarra, setAcoesBarra, acoesTravadas] = usePrefTickets(
		"acoesBarra",
		ACOES_PADRAO
	);
	const naBarra = (id) => acoesBarra.includes(id);
	const { temPermissao } = usePermissions();

	// Os botões de dentro da conversa: barra sobre as mensagens e o que fica em
	// volta do campo de escrever.
	const [botoesDaConversa, setBotoesDaConversa, botoesTravados] =
		usePrefTickets("botoesDaConversa", IDS_BOTOES_DA_CONVERSA);
	const [automacaoAberta, setAutomacaoAberta] = useState(false);

	// Este componente não desmonta ao trocar de atendimento — o pai só troca a
	// prop `ticket`. Sem sincronizar, o rótulo continuava mostrando o estado do
	// atendimento anterior até alguém recarregar a página.
	useEffect(() => {
		setPausado(Boolean(ticket.botPaused));
	}, [ticket.id, ticket.botPaused]);

	// Continuar no webchat: manda ao cliente, por esta conversa, um link que
	// abre o chat no navegador já identificado. Só conexões de webchat no ar
	// com o modo ligado; o próprio atendimento não pode ser de webchat.
	const { whatsApps } = useWhatsApps();
	const webchats = (whatsApps || []).filter(
		(w) =>
			w.apiProvider === "webchat" &&
			w.status === "CONNECTED" &&
			w.webchatConfig?.linkPorAtendimento !== false &&
			w.id !== ticket.whatsappId
	);

	const enviarLinkDoWebchat = async (conexao) => {
		setMenuOpen(false);
		try {
			await api.post(`/tickets/${ticket.id}/webchat-link`, {
				whatsappId: conexao.id,
				origem: window.location.origin,
			});
			toast.success("Link do webchat enviado.");
		} catch (err) {
			toastError(err);
		}
	};

	// Links de redirecionamento: o convite leva o cliente para a conexão onde a
	// conversa continua. Buscados ao abrir o menu, não a cada troca de
	// atendimento.
	const [linksDeRedirecionamento, setLinksDeRedirecionamento] = useState(null);

	useEffect(() => {
		if (!menuOpen || linksDeRedirecionamento) return;
		api
			.get("/links-de-redirecionamento")
			.then(({ data }) => setLinksDeRedirecionamento(data))
			.catch(() => setLinksDeRedirecionamento([]));
	}, [menuOpen, linksDeRedirecionamento]);

	const linksParaOutraConexao = (linksDeRedirecionamento || []).filter(
		(l) => l.ativo && l.whatsappId !== ticket.whatsappId
	);

	const enviarLinkDeRedirecionamento = async (link) => {
		setMenuOpen(false);
		try {
			await api.post(`/tickets/${ticket.id}/link-de-redirecionamento`, {
				linkId: link.id,
			});
			toast.success("Link enviado.");
		} catch (err) {
			toastError(err);
		}
	};

	const alternarRobo = async () => {
		const novo = !pausado;

		setMenuOpen(false);

		try {
			await api.put(`/tickets/${ticket.id}/bot`, { paused: novo });
			setPausado(novo);
			toast.success(
				novo
					? "Robô pausado nesta conversa."
					: "Robô liberado para responder aqui."
			);
		} catch (err) {
			toastError(err);
		}
	};

	const handleOpenTransferModal = () => {
		setTransferTicketModalOpen(true);
		setMenuOpen(false);
	};

	// Alt+t de transferir, pela mesma porta do item do menu. Alt para andar
	// junto com o Alt+e do encerrar: as duas teclas que mexem no atendimento
	// pedem o mesmo gesto.
	useAtalhoDeTeclado(
		"t",
		() => setTransferTicketModalOpen(true),
		ticket?.status === "open",
		{ alt: true }
	);

	const handleCloseTransferTicketModal = () => {
		if (isMounted.current) {
			setTransferTicketModalOpen(false);
		}
	};


	// Prazos da soneca. "Amanhã 9h" é montado por componentes de data, não por
	// string ISO: `new Date("aaaa-mm-dd")` é meia-noite UTC e, em fuso
	// negativo, cai no dia anterior.
	const minutosAteAmanha = () => {
		const agora = new Date();
		const amanha = new Date(
			agora.getFullYear(),
			agora.getMonth(),
			agora.getDate() + 1,
			9,
			0,
			0
		);

		return Math.max(1, Math.round((amanha.getTime() - agora.getTime()) / 60000));
	};

	const PRAZOS = [
		{ rotulo: "Em 30 minutos", minutos: 30 },
		{ rotulo: "Em 2 horas", minutos: 120 },
		{ rotulo: "Em 4 horas", minutos: 240 },
		{ rotulo: "Amanhã às 9h", minutos: null },
	];

	// Em pausa é ter prazo marcado — vencido inclusive: enquanto ninguém
	// desligar o relógio, o compromisso continua de pé e o menu tem de oferecer
	// o "trazer de volta".
	const adiado = Boolean(ticket.snoozedUntil);

	const adiar = async (minutos) => {
		setMenuOpen(false);

		try {
			await api.put(`/tickets/${ticket.id}/snooze`, { minutos });
			avisarContadores("pausa");
			toast.success(
				minutos ? "Atendimento em pausa." : "Atendimento de volta na lista."
			);
		} catch (err) {
			toastError(err);
		}
	};

	// Quanto falta da pausa, para o aviso do cabeçalho. O servidor não avisa
	// quando o prazo vence — ele limpa na próxima mensagem —, então a conta é
	// feita aqui.
	const restanteDaPausa = (() => {
		if (!pausado || !ticket.botPausedUntil) return null;

		const minutos = Math.round(
			(new Date(ticket.botPausedUntil).getTime() - Date.now()) / 60000
		);

		if (minutos <= 0) return null;
		if (minutos < 60) return `${minutos}min`;

		return `${Math.floor(minutos / 60)}h`;
	})();

	return (
		<>
			{/* Robô pausado é estado do atendimento: tem de aparecer sem abrir
			    menu nenhum. Clicar leva direto para a automação. */}
			{pausado && (
				<Button
					variant="ghost"
					size="sm"
					onClick={() => setAutomacaoAberta(true)}
					title="Robô pausado nesta conversa — clique para ajustar"
					className="h-8 gap-1.5 rounded-full bg-amber-100 px-2.5 text-amber-700 hover:bg-amber-200 dark:bg-amber-500/15 dark:text-amber-300"
				>
					<BotOff className="h-4 w-4" />
					<span className="text-xs font-medium">
						{restanteDaPausa || "pausado"}
					</span>
				</Button>
			)}
			<AdicionarAoKanbanModal
				open={funnelModalOpen}
				onClose={() => setFunnelModalOpen(false)}
				contact={ticket.contact}
			/>
			{/* Em espera, transferir exige a permissão de fazê-lo sem aceitar —
			    é o que separa quem administra o departamento de quem atende nela. */}
			{naBarra("transferir") &&
				(ticket.status === "open" ||
					(ticket.status === "pending" &&
						temPermissao("tickets:transferPending"))) && (
				<Button
					variant="ghost"
					size="icon"
					onClick={handleOpenTransferModal}
					title={i18n.t("ticketOptionsMenu.transfer")}
					aria-label={i18n.t("ticketOptionsMenu.transfer")}
				>
					<ArrowRightLeft className="h-4 w-4" />
				</Button>
			)}

			{/* Quando o robô está pausado o aviso amarelo acima já é o botão;
			    um segundo botão dizendo a mesma coisa seria ruído. */}
			{naBarra("robo") && !pausado && (
				<Button
					variant="ghost"
					size="icon"
					onClick={alternarRobo}
					title="Pausar robô (fluxo e IA)"
					aria-label="Pausar robô (fluxo e IA)"
				>
					<BotOff className="h-4 w-4" />
				</Button>
			)}

			{naBarra("automacao") && (
				<Button
					variant="ghost"
					size="icon"
					onClick={() => setAutomacaoAberta(true)}
					title="Automação da conversa"
					aria-label="Automação da conversa"
				>
					<SlidersHorizontal className="h-4 w-4" />
				</Button>
			)}

			{naBarra("kanban") && !ticket.isGroup && (
				<Button
					variant="ghost"
					size="icon"
					onClick={() => setFunnelModalOpen(true)}
					title="Adicionar ao Kanban"
					aria-label="Adicionar ao Kanban"
				>
					<KanbanSquare className="h-4 w-4" />
				</Button>
			)}

			{naBarra("replay") && (
				<Can
					role={user.profile}
					perform="ticket-options:replay"
					yes={() => (
						<Button
							variant="ghost"
							size="icon"
							onClick={() => navigate(`/tickets/${ticket.id}/replay`)}
							title="Ver replay"
							aria-label="Ver replay"
						>
							<History className="h-4 w-4" />
						</Button>
					)}
				/>
			)}

			<DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
				<DropdownMenuTrigger asChild>
					<Button variant="ghost" size="icon">
						<MoreVertical className="h-4 w-4" />
					</Button>
				</DropdownMenuTrigger>
				<DropdownMenuContent align="end">
					<DropdownMenuItem onClick={alternarRobo}>
						{pausado ? (
							<Bot className="h-4 w-4" />
						) : (
							<BotOff className="h-4 w-4" />
						)}
						{pausado ? "Reativar robô" : "Pausar robô (fluxo e IA)"}
					</DropdownMenuItem>
					<DropdownMenuItem
						onClick={() => {
							setMenuOpen(false);
							setAutomacaoAberta(true);
						}}
					>
						<SlidersHorizontal className="h-4 w-4" />
						Automação da conversa…
					</DropdownMenuItem>

					<DropdownMenuSeparator />

					{/* Pausar não encerra: o atendimento continua aberto e some da
					    lista até a hora marcada. Mensagem do cliente não cancela a
					    pausa — só pinta o relógio de amarelo. */}
					{adiado ? (
						<DropdownMenuItem onClick={() => adiar(0)}>
							<AlarmClockOff className="h-4 w-4" />
							Trazer de volta agora
						</DropdownMenuItem>
					) : (
						<DropdownMenuSub>
							<DropdownMenuSubTrigger>
								<AlarmClock className="h-4 w-4" />
								Pausar (lembrar depois)
							</DropdownMenuSubTrigger>
							<DropdownMenuSubContent>
								{PRAZOS.map((prazo) => (
									<DropdownMenuItem
										key={prazo.rotulo}
										onClick={() => adiar(prazo.minutos ?? minutosAteAmanha())}
									>
										{prazo.rotulo}
									</DropdownMenuItem>
								))}
							</DropdownMenuSubContent>
						</DropdownMenuSub>
					)}

					<DropdownMenuSeparator />
					{/* Transferir mexe em quem atende: só faz sentido com a conversa
					    aberta. Pausar o robô e a automação, acima, valem sempre. */}
					{ticket.status === "open" && (
						<DropdownMenuItem onClick={handleOpenTransferModal}>
							{i18n.t("ticketOptionsMenu.transfer")}
						</DropdownMenuItem>
					)}
					{ticket.status === "open" &&
						!ticket.isGroup &&
						webchats.map((conexao) => (
							<DropdownMenuItem
								key={`webchat-${conexao.id}`}
								onClick={() => enviarLinkDoWebchat(conexao)}
							>
								<MessageSquare className="h-4 w-4" />
								{webchats.length > 1
									? `Continuar no webchat (${conexao.name})`
									: "Continuar no webchat"}
							</DropdownMenuItem>
						))}
					{ticket.status === "open" &&
						!ticket.isGroup &&
						linksParaOutraConexao.length > 0 && (
							<DropdownMenuSub>
								<DropdownMenuSubTrigger>
									<Link2 className="h-4 w-4" />
									Levar para outra conexão
								</DropdownMenuSubTrigger>
								<DropdownMenuSubContent>
									{linksParaOutraConexao.map((link) => (
										<DropdownMenuItem
											key={`redirecionar-${link.id}`}
											onClick={() => enviarLinkDeRedirecionamento(link)}
										>
											<span className="flex flex-col">
												<span>{link.nome}</span>
												<span className="text-xs text-muted-foreground">
													{link.whatsapp?.name}
													{link.encerrarAtual ? " · encerra este atendimento ao tocar" : ""}
												</span>
											</span>
										</DropdownMenuItem>
									))}
								</DropdownMenuSubContent>
							</DropdownMenuSub>
						)}
					{/* Grupo não vira oportunidade: o negócio é com uma pessoa, e
					    o contato de um grupo é o grupo inteiro. */}
					{!ticket.isGroup && (
						<DropdownMenuItem
							onClick={() => {
								setMenuOpen(false);
								setFunnelModalOpen(true);
							}}
						>
							Adicionar ao Kanban
						</DropdownMenuItem>
					)}
					<Can
						role={user.profile}
						perform="ticket-options:replay"
						yes={() => (
							<DropdownMenuItem
								onClick={() => navigate(`/tickets/${ticket.id}/replay`)}
							>
								Ver replay
							</DropdownMenuItem>
						)}
					/>
					<Can
						role={user.profile}
						perform="ticket-options:deleteTicket"
						yes={() => (
							<DropdownMenuItem
								className="text-destructive focus:text-destructive"
								onClick={() => {
									setConfirmationOpen(true);
									setMenuOpen(false);
								}}
							>
								{i18n.t("ticketOptionsMenu.delete")}
							</DropdownMenuItem>
						)}
					/>
					<DropdownMenuSeparator />

					{/* Escolher os atalhos da barra. O que está aqui no menu não
					    sai daqui: marcar só duplica como botão. */}
					<DropdownMenuSub>
						<DropdownMenuSubTrigger>
							<SlidersHorizontal className="h-4 w-4" />
							Botões da conversa
						</DropdownMenuSubTrigger>
						<DropdownMenuSubContent>
							{BOTOES_DA_CONVERSA.map((botao) => {
								const marcado = botoesDaConversa.includes(botao.id);
								const travado = botoesTravados.includes(botao.id);

								return (
									<DropdownMenuItem
										key={botao.id}
										disabled={travado}
										title={
											travado
												? "Definido pelo cargo — fale com um administrador."
												: botao.dica
										}
										onSelect={(e) => e.preventDefault()}
										onClick={() =>
											!travado &&
											setBotoesDaConversa((atual) =>
												atual.includes(botao.id)
													? atual.filter((id) => id !== botao.id)
													: [...atual, botao.id]
											)
										}
									>
										<Check
											className={
												marcado ? "h-4 w-4 opacity-100" : "h-4 w-4 opacity-0"
											}
										/>
										{botao.rotulo}
									</DropdownMenuItem>
								);
							})}
						</DropdownMenuSubContent>
					</DropdownMenuSub>

					<DropdownMenuSub>
						<DropdownMenuSubTrigger>
							<SlidersHorizontal className="h-4 w-4" />
							Atalhos na barra
						</DropdownMenuSubTrigger>
						<DropdownMenuSubContent>
							{ACOES_DA_BARRA.map((acao) => {
								const marcada = naBarra(acao.id);
								const travada = acoesTravadas.includes(acao.id);

								return (
									<DropdownMenuItem
										key={acao.id}
										disabled={travada}
										title={
											travada
												? "Definido pelo cargo — fale com um administrador."
												: undefined
										}
										// Sem isto o menu fecha a cada marcação, e escolher
										// três atalhos vira três idas ao menu.
										onSelect={(e) => e.preventDefault()}
										// A partir do valor anterior: marcar dois atalhos
										// seguidos não pode desfazer o primeiro.
										onClick={() =>
											!travada &&
											setAcoesBarra((atual) =>
												atual.includes(acao.id)
													? atual.filter((id) => id !== acao.id)
													: [...atual, acao.id]
											)
										}
									>
										<Check
											className={
												marcada ? "h-4 w-4 opacity-100" : "h-4 w-4 opacity-0"
											}
										/>
										{acao.rotulo}
									</DropdownMenuItem>
								);
							})}
						</DropdownMenuSubContent>
					</DropdownMenuSub>
				</DropdownMenuContent>
			</DropdownMenu>
			<ConfirmationModal
				title={`${i18n.t("ticketOptionsMenu.confirmationModal.title")}${
					ticket.id
				} ${i18n.t("ticketOptionsMenu.confirmationModal.titleFrom")} ${
					// Sem o `?.` a tela inteira fica BRANCA quando o atendimento
					// não carrega — e ele não carrega sempre: a API responde 403
					// quando o ticket é movido para uma fila que a pessoa não vê,
					// e aí `ticket` fica {}. O título do modal derrubava o painel
					// por causa de um nome que nem estava à vista.
					ticket?.contact?.name || ""
				}?`}
				open={confirmationOpen}
				onClose={setConfirmationOpen}
				onConfirm={handleDeleteTicket}
			>
				{i18n.t("ticketOptionsMenu.confirmationModal.message")}
			</ConfirmationModal>
			<TransferTicketModal
				modalOpen={transferTicketModalOpen}
				onClose={handleCloseTransferTicketModal}
				ticketid={ticket.id}
				ticketWhatsappId={ticket.whatsappId}
			/>
			<TicketAutomationModal
				open={automacaoAberta}
				onClose={() => setAutomacaoAberta(false)}
				ticket={ticket}
				onSaved={(atualizado) => setPausado(Boolean(atualizado?.botPaused))}
			/>
		</>
	);
};

export default TicketOptionsMenu;
